import { useEffect, useReducer, useRef, useState } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import { ImageToolbarButton } from '@/components/ImageToolbarButton'
import { RichTextListButtons } from '@/components/RichTextListButtons'
import { buildRichTextExtensions } from '@/lib/richTextExtensions'
import {
  handleRichTextImageDrop,
  handleRichTextImagePaste,
} from '@/lib/richTextImages'

function ToolbarButton({ active, disabled, onClick, children, title }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={[
        'rounded border px-2 py-1 text-xs font-semibold',
        active ? 'border-brand bg-brand text-white' : 'border-slate-300 bg-white text-brand hover:bg-slate-50',
        disabled ? 'opacity-40' : '',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

/** TipTap editor for template seed HTML (local state only). */
export function SeedHtmlEditor({ value, onChange, disabled, resetKey }) {
  const [, bump] = useReducer((n) => n + 1, 0)
  const [error, setError] = useState('')
  const editorRef = useRef(null)

  const editor = useEditor({
    extensions: buildRichTextExtensions(),
    content: value || '<p></p>',
    editable: !disabled,
    editorProps: {
      attributes: {
        class: 'prose-kb min-h-[12rem] max-w-none px-3 py-2 focus:outline-none',
      },
      handlePaste: (_view, event) =>
        handleRichTextImagePaste(editorRef.current, _view, event, setError),
      handleDrop: (_view, event) =>
        handleRichTextImageDrop(editorRef.current, _view, event, setError),
    },
    onSelectionUpdate: () => bump(),
    onTransaction: () => bump(),
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.getHTML())
    },
  })

  useEffect(() => {
    editorRef.current = editor
  }, [editor])

  useEffect(() => {
    if (!editor) return
    editor.setEditable(!disabled)
  }, [editor, disabled])

  useEffect(() => {
    if (!editor) return
    editor.commands.setContent(value || '<p></p>', false)
    // resetKey changes when switching category / reloading template
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, resetKey])

  if (!editor) return null

  const inTable = Boolean(editor.isActive('table'))

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      {error && (
        <p className="border-b border-red-100 bg-red-50 px-2 py-1.5 text-xs text-red-700" role="alert">
          {error}
        </p>
      )}
      {!disabled && (
        <div className="flex flex-wrap gap-2 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
          <ToolbarButton
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            Bold
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            Underline
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive('heading', { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            Heading
          </ToolbarButton>
          <RichTextListButtons editor={editor} ToolbarButton={ToolbarButton} />
          <ToolbarButton
            active={inTable}
            onClick={() =>
              editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
            }
          >
            Table
          </ToolbarButton>
          <ToolbarButton
            disabled={!inTable}
            onClick={() => editor.chain().focus().addColumnAfter().run()}
          >
            + Col
          </ToolbarButton>
          <ToolbarButton
            disabled={!inTable}
            onClick={() => editor.chain().focus().deleteColumn().run()}
          >
            − Col
          </ToolbarButton>
          <ToolbarButton
            disabled={!inTable}
            onClick={() => editor.chain().focus().addRowAfter().run()}
          >
            + Row
          </ToolbarButton>
          <ToolbarButton
            disabled={!inTable}
            onClick={() => editor.chain().focus().deleteRow().run()}
          >
            − Row
          </ToolbarButton>
          <ToolbarButton
            disabled={!inTable}
            onClick={() => editor.chain().focus().deleteTable().run()}
          >
            Delete table
          </ToolbarButton>
          <ImageToolbarButton
            editor={editor}
            ToolbarButton={ToolbarButton}
            onError={setError}
          />
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  )
}
